import HeaderDictionary from '@/types/dictionaries/Header';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CompanyIcon from '@/components/Icons/CompanyIcon';
import Grid from '@mui/material/Unstable_Grid2';
import { useAccountDataStore } from '@/utils/providers/AccountDataStoreProvider';
import { useShallow } from 'zustand/react/shallow';
import { useEllipsisStyles } from '@/hooks/useEllipsisStyles';

interface BillingProfileMenuHeaderProps {
	dict: HeaderDictionary;
	accountMenuType: 'drawer' | 'dropdown';
	onSwitchBillingProfile: () => void;
}

export default function BillingProfileMenuHeader({
	dict,
	accountMenuType,
	onSwitchBillingProfile,
}: Readonly<BillingProfileMenuHeaderProps>) {
	const { userName, businessUnitName, canSwitchBillingProfiles } = useAccountDataStore(
		useShallow((state) => ({
			userName: `${state.firstName} ${state.lastName}`,
			businessUnitName: state.selectedBillingProfile?.addressContact.businessUnitName,
			canSwitchBillingProfiles: state.billingProfiles.length >= 2,
		})),
	);
	const ellipsisStyles = useEllipsisStyles();

	return (
		<Box borderBottom="1px solid" borderColor="grey.light">
			<Button
				onClick={onSwitchBillingProfile}
				sx={{
					display: 'block',
					textAlign: 'left',
					width: '100%',
					px: '20px',
					py: '16px',
					borderRadius: '0px',
				}}
				disabled={!canSwitchBillingProfiles}
				aria-labelledby="billing-profile-menu-header-label"
			>
				<Box component="span" display="flex" mb="14px">
					<Avatar
						component="span"
						sx={{
							backgroundColor: 'primary.main',
							color: 'white.main',
							width: '56px',
							height: '56px',
							my: 'auto',
							mr: '16px',
						}}
					>
						<CompanyIcon />
					</Avatar>
					<Grid
						component="span"
						container
						direction="column"
						width={accountMenuType === 'drawer' ? '100%' : 'min-content'}
						gap="4px"
					>
						<Box
							component="span"
							width={accountMenuType === 'drawer' ? '100%' : 'max-content'}
							color="black.main"
							id="billing-profile-menu-header-label"
						>
							{canSwitchBillingProfiles && (
								<>
									<Typography variant="h7SemiBold">{dict.switch}</Typography>
									<br />
								</>
							)}
							<Typography component="span" variant="h4">
								{dict.billingProfiles}
							</Typography>
						</Box>
						<Typography
							variant="legalBold"
							color="grey.dark"
							sx={{ ...ellipsisStyles, display: 'block', whiteSpace: 'nowrap', maxWidth: '40ch' }}
						>
							{businessUnitName}
						</Typography>
					</Grid>
				</Box>
				<Typography
					variant="legal"
					color="grey.main"
					sx={{ ...ellipsisStyles, display: 'block', whiteSpace: 'nowrap', maxWidth: '40ch' }}
				>
					{userName}
				</Typography>
			</Button>
		</Box>
	);
}
